import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { slug: "", name: "", type: "normal", audio_policy: "auto", failback_window: 600, enabled: true, sources: [{ url: "", label: "", enabled: true }], push_targets: [] };
const inp = "bg-[#0B111B] border-slate-700 h-8 text-sm";

function Field({ label, children }) {
  return <label className="block space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>{children}</label>;
}

function SourceRow({ s, i, n, set, move, remove, labs }) {
  return (
    <div className="grid grid-cols-[28px_1fr_140px_auto] gap-2 items-center" data-testid={`source-row-${i}`}>
      <span className="font-mono text-xs text-cyan-400 text-center">#{i + 1}</span>
      <div className="flex gap-1">
        <Input data-testid={`source-url-input-${i}`} className={`${inp} font-mono text-xs`} placeholder="http(s)://… HLS or TS (any extension)" value={s.url} onChange={(e) => set(i, { url: e.target.value })} />
        {labs.length > 0 && (
          <Select onValueChange={(v) => { const [id, k] = v.split("|"); const l = labs.find((x) => x.id === id); set(i, { url: l.urls[k], label: s.label || `${l.name} ${k.toUpperCase()}` }); }}>
            <SelectTrigger data-testid={`source-lab-pick-${i}`} className="w-24 h-8 bg-[#0B111B] border-slate-700 text-xs"><SelectValue placeholder="Lab…" /></SelectTrigger>
            <SelectContent className="bg-[#16202E] border-slate-700">
              {labs.flatMap((l) => ["hls", "ts", "php", "redirect"].map((k) => <SelectItem key={l.id + k} value={`${l.id}|${k}`}>{l.name} · {k}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>
      <Input data-testid={`source-label-input-${i}`} className={inp} placeholder="label" value={s.label} onChange={(e) => set(i, { label: e.target.value })} />
      <div className="flex items-center gap-1">
        <Switch data-testid={`source-enabled-${i}`} checked={s.enabled} onCheckedChange={(v) => set(i, { enabled: v })} />
        <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-100 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button type="button" onClick={() => move(i, 1)} disabled={i === n - 1} className="p-1 text-slate-400 hover:text-slate-100 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button type="button" data-testid={`source-remove-${i}`} onClick={() => remove(i)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

function PushRow({ t, i, set, remove }) {
  return (
    <div className="grid grid-cols-[1fr_180px_auto] gap-2 items-center" data-testid={`push-row-${i}`}>
      <Input data-testid={`push-url-input-${i}`} className={`${inp} font-mono text-xs`} placeholder="rtmp://server/app" value={t.url} onChange={(e) => set(i, { url: e.target.value })} />
      <Input data-testid={`push-key-input-${i}`} className={`${inp} font-mono text-xs`} placeholder="stream key" value={t.key} onChange={(e) => set(i, { key: e.target.value })} />
      <div className="flex items-center gap-1">
        <Switch data-testid={`push-enabled-${i}`} checked={t.enabled} onCheckedChange={(v) => set(i, { enabled: v })} />
        <button type="button" onClick={() => remove(i)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

export function ChannelForm({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [labs, setLabs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial } : EMPTY);
    api.get("/admin/lab").then(({ data }) => setLabs(data)).catch(() => {});
  }, [open, initial]);

  const up = (p) => setF((x) => ({ ...x, ...p }));
  const listOps = (key) => ({
    set: (i, p) => setF((x) => ({ ...x, [key]: x[key].map((s, j) => (j === i ? { ...s, ...p } : s)) })),
    remove: (i) => setF((x) => ({ ...x, [key]: x[key].filter((_, j) => j !== i) })),
    move: (i, d) => setF((x) => { const a = [...x[key]]; [a[i], a[i + d]] = [a[i + d], a[i]]; return { ...x, [key]: a }; }),
  });
  const src = listOps("sources");
  const push = listOps("push_targets");

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, failback_window: Number(f.failback_window), sources: f.sources.filter((s) => s.url.trim()) };
      const { data } = initial ? await api.put(`/admin/channels/${initial.id}`, body) : await api.post("/admin/channels", body);
      toast.success(initial ? "Channel updated" : "Channel created");
      onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="channel-form-dialog" className="max-w-3xl bg-[#0F1722] border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{initial ? `Edit ${initial.name}` : "New channel"}</DialogTitle><DialogDescription className="text-xs text-slate-400">Configure the source ladder, audio policy and RTMP push targets.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input data-testid="channel-name-input" className={inp} value={f.name} onChange={(e) => up({ name: e.target.value })} /></Field>
          <Field label="Slug"><Input data-testid="channel-slug-input" className={`${inp} font-mono`} value={f.slug} onChange={(e) => up({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} /></Field>
          <Field label="Channel type">
            <Select value={f.type} onValueChange={(v) => up({ type: v })}>
              <SelectTrigger data-testid="channel-type-select" className={inp}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#16202E] border-slate-700"><SelectItem value="normal">Normal (freeze 6 s / 10 s)</SelectItem><SelectItem value="news">News (freeze 20 s)</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Audio policy">
            <Select value={f.audio_policy} onValueChange={(v) => up({ audio_policy: v })}>
              <SelectTrigger data-testid="channel-audio-select" className={inp}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#16202E] border-slate-700">
                <SelectItem value="auto">Auto (passthrough, convert AC-3/E-AC-3/MP2 or mixed ladder)</SelectItem>
                <SelectItem value="passthrough">Always passthrough</SelectItem>
                <SelectItem value="aac">Always AAC 48 kHz stereo</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Failback window (seconds stable)"><Input data-testid="channel-failback-input" type="number" min={10} className={`${inp} font-mono`} value={f.failback_window} onChange={(e) => up({ failback_window: e.target.value })} /></Field>
          <div className="flex items-end gap-2 pb-1"><Switch data-testid="channel-enabled-switch" checked={f.enabled} onCheckedChange={(v) => up({ enabled: v })} /><span className="text-sm text-slate-300">Run channel</span></div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Source ladder (priority order, max 5)</span>
            <Button data-testid="add-source-btn" type="button" size="sm" variant="ghost" disabled={f.sources.length >= 5} onClick={() => up({ sources: [...f.sources, { url: "", label: "", enabled: true }] })}><Plus size={14} /> Source</Button>
          </div>
          {f.sources.map((s, i) => <SourceRow key={i} s={s} i={i} n={f.sources.length} labs={labs} {...src} />)}
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">RTMP push targets (max 3)</span>
            <Button data-testid="add-push-btn" type="button" size="sm" variant="ghost" disabled={f.push_targets.length >= 3} onClick={() => up({ push_targets: [...f.push_targets, { url: "", key: "", enabled: false }] })}><Plus size={14} /> Target</Button>
          </div>
          {f.push_targets.length === 0 && <p className="text-xs text-slate-500">No push targets.</p>}
          {f.push_targets.map((t, i) => <PushRow key={i} t={t} i={i} {...push} />)}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="channel-save-btn" disabled={busy} onClick={save} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">{busy ? "Saving…" : "Save channel"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
